/**
 * Resolve item names on bill/invoice line items.
 * Handles legacy rows (itemId-only, denormalized names, stale master refs).
 */

export function getBillLineItems(bill) {
  if (!bill) return [];
  const source =
    Array.isArray(bill.rows) && bill.rows.length > 0
      ? bill.rows
      : Array.isArray(bill.items)
        ? bill.items
        : [];
  return source.filter((row) => row && typeof row === 'object');
}

export function getBillLineItemId(line) {
  if (!line || typeof line !== 'object') return '';
  return line.item || line.itemId || line.id || line.productId || '';
}

export function getItemDisplayName(item) {
  if (!item || typeof item !== 'object') return '';
  return String(item.itemName || item.name || item.description || '').trim();
}

export function findItemInMaster(itemId, items = []) {
  if (!itemId || !Array.isArray(items) || items.length === 0) return null;
  const idStr = String(itemId).trim();
  if (!idStr) return null;

  const list = items.filter((i) => i && typeof i === 'object');

  let found = list.find((i) => i.id === itemId || i.itemId === itemId);
  if (found) return found;

  found = list.find((i) => String(i.id) === idStr || String(i.itemId) === idStr);
  if (found) return found;

  const lower = idStr.toLowerCase();
  return (
    list.find((i) => {
      const name = getItemDisplayName(i).toLowerCase();
      return name && name === lower;
    }) || null
  );
}

/**
 * Match a typed search string to an item in the master list.
 * @param {'exact'|'prefix'|'fuzzy'} mode
 *   - exact: exact name/code/hsn only (use while typing)
 *   - prefix: exact first, then unique startsWith (use on blur)
 *   - fuzzy: exact → unique startsWith → unique includes
 * Returns null when no match, or when prefix/includes is ambiguous.
 */
export function matchItemBySearchText(text, items = [], mode = 'exact') {
  const query = String(text || '').trim().toLowerCase();
  if (!query || !Array.isArray(items) || items.length === 0) return null;

  const list = items.filter((i) => i && typeof i === 'object');
  const getCode = (it) => String(it.code || '').trim().toLowerCase();
  const getHsn = (it) => String(it.hsn || it.hsnCode || '').trim().toLowerCase();
  const getName = (it) => getItemDisplayName(it).toLowerCase();

  const exact = list.find(
    (it) => getName(it) === query || getCode(it) === query || getHsn(it) === query
  );
  if (exact) return exact;
  if (mode === 'exact') return null;

  const startsWithMatches = list.filter(
    (it) =>
      (getName(it) && getName(it).startsWith(query)) ||
      (getCode(it) && getCode(it).startsWith(query)) ||
      (getHsn(it) && getHsn(it).startsWith(query))
  );
  if (startsWithMatches.length === 1) return startsWithMatches[0];
  if (mode === 'prefix') return null;

  const includesMatches = list.filter(
    (it) =>
      (getName(it) && getName(it).includes(query)) ||
      (getCode(it) && getCode(it).includes(query)) ||
      (getHsn(it) && getHsn(it).includes(query))
  );
  if (includesMatches.length === 1) return includesMatches[0];
  return null;
}

/** Filter items for picker/datalist — prefer search-filtered full list over arbitrary caps. */
export function filterItemsForPicker(items = [], searchText = '', maxWhenEmpty = 500) {
  if (!Array.isArray(items)) return [];
  const list = items.filter((i) => i && typeof i === 'object');
  const q = String(searchText || '').trim().toLowerCase();
  if (!q) return list.slice(0, maxWhenEmpty);
  return list.filter((it) => {
    const name = getItemDisplayName(it).toLowerCase();
    const code = String(it.code || '').toLowerCase();
    const hsn = String(it.hsn || it.hsnCode || '').toLowerCase();
    return name.includes(q) || code.includes(q) || hsn.includes(q);
  });
}

export function resolveBillLineItemName(line, items = [], fallback = 'Unnamed Item') {
  if (!line || typeof line !== 'object') return fallback;

  const rowName = [line.itemName, line.name, line.productName, line.product].find(
    (v) => v && String(v).trim()
  );

  const itemId = getBillLineItemId(line);
  const master = findItemInMaster(itemId, items);
  if (master) {
    return getItemDisplayName(master) || String(rowName || '').trim() || fallback;
  }

  if (rowName) return String(rowName).trim();

  return fallback;
}

/**
 * Ensure a row has a resolvable item id before save.
 * Tries search text / denormalized name when item id is missing.
 */
export function resolveRowItemBeforeSave(row, items = [], searchText = '') {
  if (!row || typeof row !== 'object') return { ok: false, row: row || {} };

  const existingId = getBillLineItemId(row);
  if (existingId) {
    const master = findItemInMaster(existingId, items);
    if (master) {
      return {
        ok: true,
        row: {
          ...row,
          item: master.id || existingId,
          itemName: getItemDisplayName(master) || row.itemName || row.name || '',
        },
      };
    }
    // Keep orphaned id but try to keep any denormalized name
    if (row.itemName || row.name) {
      return { ok: true, row: { ...row, item: existingId, itemName: row.itemName || row.name } };
    }
  }

  const label = searchText || row.itemName || row.name || '';
  const match = matchItemBySearchText(label, items, 'fuzzy');
  if (match) {
    return {
      ok: true,
      row: {
        ...row,
        item: match.id,
        itemName: getItemDisplayName(match),
      },
    };
  }

  return { ok: false, row };
}

export function enrichBillRowForSave(row, items = []) {
  if (!row || typeof row !== 'object') return row || {};
  const { subParty: _legacySubParty, ...rest } = row;
  const resolved = resolveRowItemBeforeSave(rest, items);
  if (resolved.ok) {
    const { subParty: _sp, ...clean } = resolved.row;
    return clean;
  }

  const itemId = getBillLineItemId(rest);
  if (!itemId) return rest;

  const master = findItemInMaster(itemId, items);
  const itemName = getItemDisplayName(master) || rest.itemName || rest.name || '';
  if (!itemName) {
    return { ...rest, item: rest.item || itemId };
  }

  return {
    ...rest,
    item: rest.item || itemId,
    itemName,
  };
}

export function enrichBillRowForPrint(row, items = []) {
  if (!row || typeof row !== 'object') return row || {};
  const itemId = getBillLineItemId(row);
  const master = findItemInMaster(itemId, items);
  return {
    ...row,
    description: resolveBillLineItemName(row, items, ''),
    hsn: master?.hsnCode || master?.hsn || row.hsn || row.hsnCode || '',
  };
}
